import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { setAuthenticated } from '../../utils/Auth';
import './NoMatch.css';


// 导入真实 API 和模拟 API
import * as realApi from '../../api/noMatchApi';
import * as mockApi from '../../api/mockNoMatchApi';

// 控制是否使用模拟数据
const USE_MOCK_API = false; // 设置为 true 使用模拟数据，设置为 false 使用真实 API

// 选择要使用的 API
const api = USE_MOCK_API ? mockApi : realApi;

const NoMatch = () => {
  const navigate = useNavigate();
  
  // 状态管理
  const [user, setUser] = useState(null);
  const [showInitModal, setShowInitModal] = useState(false);
  const [chatRooms, setChatRooms] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isMatching, setIsMatching] = useState(false);
  const [error, setError] = useState(null);
  
  // 初始弹窗表单状态
  const [userName, setUserName] = useState('');
  const [userGender, setUserGender] = useState('prefer_not_to_say'); // 用戶自己的性別
  const [userPreferences, setUserPreferences] = useState({
    preferredGender: 'any', // 配對偏好
    // 保持向後相容性
    gender: 'any'
  });

  // 加载聊天室列表 - 使用 useCallback 避免不必要的重新创建
  const loadChatRooms = useCallback(async () => {
    try {
      const response = await api.getChatRooms();
      if (response.success) {
        // 去重逻辑：根据 roomId 去重
        const uniqueChatRooms = response.chatRooms.filter((room, index, self) => 
          index === self.findIndex(r => r.id === room.id)
        );
        
        console.log('📋 Chat rooms loaded:', {
          total: response.chatRooms.length,
          unique: uniqueChatRooms.length,
          rooms: uniqueChatRooms
        });
        
        setChatRooms(uniqueChatRooms);
      }
    } catch (error) {
      console.error('Error loading chat rooms:', error);
      setError('無法載入聊天室列表');
    }
  }, []); // 空依赖数组，因为函数内部没有使用任何状态或props

  // 组件挂载时初始化
  useEffect(() => {
    checkUserStatus();
  }, []); // 只在组件挂载时执行一次

  // 监听页面焦点变化
  useEffect(() => {
    const handleFocus = () => {
      if (user) {
        console.log('🔄 Page focused, reloading chat rooms');
        loadChatRooms();
      }
    };
    
    window.addEventListener('focus', handleFocus);
    
    return () => {
      window.removeEventListener('focus', handleFocus);
    };
  }, [user, loadChatRooms]); // 依赖 user 和 loadChatRooms

  // 监听路由变化
  useEffect(() => {
    const handleRouteChange = () => {
      if (window.location.pathname === '/nomatch' && user) {
        console.log('🔄 Route changed, refreshing chat rooms');
        loadChatRooms();
      }
    };

    // 监听浏览器的后退/前进按钮
    window.addEventListener('popstate', handleRouteChange);
    
    return () => {
      window.removeEventListener('popstate', handleRouteChange);
    };
  }, [user, loadChatRooms]); // 依赖 user 和 loadChatRooms

  // 检查用户状态
  const checkUserStatus = async () => {
    try {
      setIsLoading(true);
      const userProfile = await api.getUserProfile();
      
      if (userProfile) {
        setUser(userProfile);
        await loadChatRooms();
      } else {
        setShowInitModal(true);
      }
    } catch (error) {
      console.error('Error checking user status:', error);
      setShowInitModal(true);
    } finally {
      setIsLoading(false);
    }
  };

  // 处理初始设置提交
  const handleInitialSetup = async (e) => {
    e.preventDefault();
    
    if (!userName.trim()) {
      alert('請輸入您的名字');
      return;
    }

    try {
      setIsLoading(true);
      
      const userData = {
        name: userName.trim(),
        userGender: userGender,
        preferences: {
          preferredGender: userPreferences.preferredGender,
          // 保持向後相容性
          gender: userPreferences.preferredGender
        },
      };
      
      const token = await api.saveUserProfile(userData);
      
      if (token) {
        setAuthenticated(token);
        setUser(userData);
        setShowInitModal(false);
        await loadChatRooms();
        
        // 自动开始匹配
        await startMatchingAfterSetup();
      }
    } catch (error) {
      console.error('Error saving user profile:', error);
      alert('保存用戶資料時發生錯誤，請重試');
    } finally {
      setIsLoading(false);
    }
  };

  // 设置完成后自动开始匹配
  const startMatchingAfterSetup = async () => {
    try {
      setIsMatching(true);
      setError(null);
      
      const response = await api.requestMatching();
      
      if (response.success && response.matched) {
        console.log('✅ Matching successful:', response);
        
        // 先刷新聊天室列表
        await loadChatRooms();
        
        // 短暂延迟后跳转到新聊天室
        setTimeout(() => {
          navigate(`/nomatch/chat/${response.roomId}`);
        }, 500);
      } else {
        setError('暫時沒有找到合適的配對，您可以稍後再試或查看現有聊天室');
      }
    } catch (error) {
      console.error('Error requesting match after setup:', error);
      setError('自動配對失敗，您可以手動點擊配對按鈕重試');
    } finally {
      setIsMatching(false);
    }
  };

  // 开始匹配
  const startMatching = async () => {
    try {
      setIsMatching(true);
      setError(null);
      
      const response = await api.requestMatching();
      
      if (response.success && response.matched) {
        console.log('✅ Matching successful:', response);
        
        // 先刷新聊天室列表
        await loadChatRooms();
        
        // 短暂延迟后跳转到新聊天室
        setTimeout(() => {
          navigate(`/nomatch/chat/${response.roomId}`);
        }, 500);
      } else {
        setError('暫時沒有找到合適的配對，請稍後再試');
      }
    } catch (error) {
      console.error('Error requesting match:', error);
      setError('配對請求失敗，請稍後再試');
    } finally {
      setIsMatching(false);
    }
  };

  // 进入聊天室
  const enterChatRoom = (roomId) => {
    console.log('🚪 Entering chat room:', roomId);
    navigate(`/nomatch/chat/${roomId}`);
  };

  // 前往设置页面
  const goToSettings = () => {
    navigate('/nomatch/settings');
  };

  // 格式化时间
  const formatTime = (timeString) => {
    const time = new Date(timeString);
    const now = new Date();
    const diff = now - time;
    
    if (diff < 60000) { // 小于1分钟
      return '剛剛';
    } else if (diff < 3600000) { // 小于1小时
      return `${Math.floor(diff / 60000)}分鐘前`;
    } else if (diff < 86400000) { // 小于1天
      return `${Math.floor(diff / 3600000)}小時前`;
    } else {
      return time.toLocaleDateString('zh-TW');
    }
  };

  // 计算剩余时间
  const getTimeLeft = (expiresAt) => {
    const now = new Date();
    const expires = new Date(expiresAt);
    const diff = expires - now;
    
    if (diff <= 0) return '已過期';
    
    const hours = Math.floor(diff / 3600000);
    const minutes = Math.floor((diff % 3600000) / 60000);
    
    if (hours > 0) {
      return `${hours}小時${minutes}分鐘後過期`;
    } else {
      return `${minutes}分鐘後過期`;
    }
  };

  if (isLoading) {
    return (
      <div className="nomatch-loading">
        <div className="loading-spinner"></div>
        <p>載入中...</p>
      </div>
    );
  }

  return (
    <div className="nomatch-container">
      {/* 初始设置弹窗 */}
      {showInitModal && (
        <div className="modal-overlay">
          <div className="init-modal">
            <h2>歡迎使用 NoMatch</h2>
            <p>請完成初始設置，我們將自動為您尋找聊天對象</p>
            
            <form onSubmit={handleInitialSetup} className="init-form">
              <div className="form-group">
                <label htmlFor="userName">您的名字：</label>
                <input
                  type="text"
                  id="userName"
                  value={userName}
                  onChange={(e) => setUserName(e.target.value)}
                  placeholder="請輸入您的名字"
                  required
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="userGender">您的性別：</label>
                <select
                  id="userGender"
                  value={userGender}
                  onChange={(e) => setUserGender(e.target.value)}
                >
                  <option value="prefer_not_to_say">不願意說明</option>
                  <option value="male">男性</option>
                  <option value="female">女性</option>
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="preferredGender">配對偏好：</label>
                <select
                  id="preferredGender"
                  value={userPreferences.preferredGender}
                  onChange={(e) => setUserPreferences({
                    ...userPreferences,
                    preferredGender: e.target.value
                  })}
                >
                  <option value="any">任何人</option>
                  <option value="male">男性</option>
                  <option value="female">女性</option>
                </select>
              </div>
              
              <button type="submit" className="submit-btn" disabled={isLoading || isMatching}>
                {isLoading 
                  ? '設置中...' 
                  : isMatching 
                    ? '配對中...' 
                    : '完成設置並開始配對'
                }
              </button>
            </form>
          </div>
        </div>
      )}

      {/* 主界面 */}
      {user && (
        <div className="nomatch-main">
          {/* AI 风格的头部 */}
          <header className="ai-header">
            <div className="header-placeholder">
              {/* 左侧占位，保持布局平衡 */}
            </div>
            <h2 className="ai-title">NoMatch 匿名聊天 {USE_MOCK_API ? '(演示模式)' : ''}</h2>
            <button onClick={goToSettings} className="menu-btn" title="設置">
              <i className="fas fa-bars"></i>
            </button>
          </header>

          {/* 匹配按钮 */}
          <div className="matching-section">
            <button 
              onClick={startMatching} 
              className="match-btn"
              disabled={isMatching}
            >
              {isMatching ? '配對中...' : '開始新的配對'}
            </button>
            
            {error && (
              <div className="error-message">
                {error}
              </div>
            )}
          </div>

          {/* 聊天室列表 */}
          <div className="chatroom-list">
            <h2>活躍聊天室 ({chatRooms.length})</h2>
            
            {chatRooms.length === 0 ? (
              <div className="empty-state">
                <p>您還沒有活躍的聊天室</p>
                <p>點擊上方按鈕開始新的配對</p>
              </div>
            ) : (
              <div className="chatroom-grid">
                {chatRooms.map((room, index) => (
                  <div 
                    key={room.id} 
                    className="chatroom-card"
                    onClick={() => enterChatRoom(room.id)}
                  >
                    <div className="chatroom-header">
                      <img 
                        src={room.partnerAvatar} 
                        alt={room.partnerName}
                        className="partner-avatar"
                        onError={(e) => {
                          e.target.src = `https://api.dicebear.com/7.x/avataaars/svg?seed=${room.partnerName}`;
                        }}
                      />
                      <div className="chatroom-info">
                        <h3>{room.partnerName}</h3>
                        <p className="last-message">{room.lastMessage}</p>
                      </div>
                    </div>
                    
                    <div className="chatroom-meta">
                      <span className="last-time">
                        {formatTime(room.lastMessageTime)}
                      </span>
                      <span className="expires-info">
                        {getTimeLeft(room.expiresAt)}
                      </span>
                    </div>
                    
                    {/* 调试信息 - 只在开发环境显示 */}
                    {process.env.NODE_ENV === 'development' && (
                      <div className="debug-info" style={{ fontSize: '10px', color: '#666', padding: '5px' }}>
                        ID: {room.id} | Index: {index}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default NoMatch; 